import React, { useState } from 'react';
import { useCustomerAuth } from '../../../contexts/CustomerAuthContext';
import { STEPS } from '../CheckoutModal';

const AuthStep = ({ state, dispatch, storeData, storeSlug, goToStep, goBack, customerAuth }) => {
  const [activeTab, setActiveTab] = useState('signin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if authentication is required (no guest checkout allowed)
  const requireAuth = storeData?.requireAccountRegistration === true;

  // Sign In fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Create Account fields
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');

  const { login, register } = useCustomerAuth();

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');

    if (!loginEmail.trim() || !loginPassword) {
      setError('Please enter both email and password.');
      return;
    }

    if (!validateEmail(loginEmail.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const result = await login(loginEmail.trim(), loginPassword, storeSlug);

      if (result.success) {
        const customerData = result.data?.customer || {};
        dispatch({
          type: 'SET_FIELDS',
          fields: {
            email: customerData.email || loginEmail.trim(),
            fullName: [customerData.firstName, customerData.lastName].filter(Boolean).join(' '),
            whatsappNumber: customerData.phone || '',
            customerId: customerData._id || customerData.id || null
          }
        });
        goToStep(STEPS.PURCHASE_INFO);
      } else {
        setError(result.error || 'Sign in failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!regFirstName.trim()) {
      setError('First name is required.');
      return;
    }

    if (!regEmail.trim()) {
      setError('Email address is required.');
      return;
    }

    if (!validateEmail(regEmail.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate password strength (must match backend requirements)
    if (!regPassword || regPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Z]/.test(regPassword)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!/[a-z]/.test(regPassword)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    if (!/[0-9]/.test(regPassword)) {
      setError('Password must contain at least one number.');
      return;
    }
    if (!/[@$!%*?&#+=-_.,;:]/.test(regPassword)) {
      setError('Password must contain at least one special character (@$!%*?&#+=-_.,;:)');
      return;
    }

    setLoading(true);
    try {
      const result = await register(
        {
          firstName: regFirstName.trim(),
          lastName: regLastName.trim(),
          email: regEmail.trim(),
          phone: regPhone.trim(),
          password: regPassword
        },
        storeSlug
      );

      if (result.success) {
        const customerData = result.data?.customer || {};
        dispatch({
          type: 'SET_FIELDS',
          fields: {
            email: customerData.email || regEmail.trim(),
            fullName: [customerData.firstName || regFirstName.trim(), customerData.lastName || regLastName.trim()].filter(Boolean).join(' '),
            whatsappNumber: customerData.phone || regPhone.trim(),
            customerId: customerData._id || customerData.id || null
          }
        });
        goToStep(STEPS.PURCHASE_INFO);
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cm-step">
      <div className="cm-auth-tabs">
        <button
          type="button"
          className={`cm-auth-tab${activeTab === 'signin' ? ' active' : ''}`}
          onClick={() => { setActiveTab('signin'); setError(''); }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          Sign In
        </button>
        <button
          type="button"
          className={`cm-auth-tab${activeTab === 'register' ? ' active' : ''}`}
          onClick={() => { setActiveTab('register'); setError(''); }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
          Create Account
        </button>
      </div>

      {error && (
        <div className="cm-auth-error">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {activeTab === 'signin' && (
        <form className="cm-form" onSubmit={handleSignIn}>
          <div className="cm-form-group">
            <label className="cm-label" htmlFor="cm-auth-login-email">Email Address</label>
            <input
              id="cm-auth-login-email"
              className="cm-input"
              type="email"
              placeholder="you@example.com"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="cm-form-group">
            <label className="cm-label" htmlFor="cm-auth-login-password">Password</label>
            <input
              id="cm-auth-login-password"
              className="cm-input"
              type="password"
              placeholder="Enter your password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="cm-auth-actions">
            <button
              type="submit"
              className="cm-btn cm-btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? (
                <span className="cm-btn-loading">
                  <span className="cm-spinner" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
            {!requireAuth && (
              <button
                type="button"
                className="cm-guest-btn"
                onClick={() => goToStep(STEPS.VISITOR_INFO)}
              >
                Continue as Guest
              </button>
            )}
          </div>
        </form>
      )}

      {activeTab === 'register' && (
        <form className="cm-form" onSubmit={handleRegister}>
          <div className="cm-form-row">
            <div className="cm-form-group">
              <label className="cm-label" htmlFor="cm-auth-reg-first">First Name</label>
              <input
                id="cm-auth-reg-first"
                className="cm-input"
                type="text"
                placeholder="First name"
                value={regFirstName}
                onChange={(e) => setRegFirstName(e.target.value)}
                required
                autoFocus
                disabled={loading}
              />
            </div>
            <div className="cm-form-group">
              <label className="cm-label" htmlFor="cm-auth-reg-last">Last Name</label>
              <input
                id="cm-auth-reg-last"
                className="cm-input"
                type="text"
                placeholder="Last name"
                value={regLastName}
                onChange={(e) => setRegLastName(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="cm-form-group">
            <label className="cm-label" htmlFor="cm-auth-reg-email">Email Address</label>
            <input
              id="cm-auth-reg-email"
              className="cm-input"
              type="email"
              placeholder="you@example.com"
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="cm-form-group">
            <label className="cm-label" htmlFor="cm-auth-reg-phone">Phone Number</label>
            <input
              id="cm-auth-reg-phone"
              className="cm-input"
              type="tel"
              placeholder="+234 800 000 0000"
              value={regPhone}
              onChange={(e) => setRegPhone(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="cm-form-group">
            <label className="cm-label" htmlFor="cm-auth-reg-password">Password</label>
            <input
              id="cm-auth-reg-password"
              className="cm-input"
              type="password"
              placeholder="Min 8 chars, upper, lower, number, special"
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading}
            />
            <small className="cm-password-hint">Must include uppercase, lowercase, number, and special character</small>
          </div>

          <div className="cm-auth-actions">
            <button
              type="submit"
              className="cm-btn cm-btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? (
                <span className="cm-btn-loading">
                  <span className="cm-spinner" />
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
            {!requireAuth && (
              <button
                type="button"
                className="cm-guest-btn"
                onClick={() => goToStep(STEPS.VISITOR_INFO)}
              >
                Continue as Guest
              </button>
            )}
          </div>
        </form>
      )}

      <div className="cm-step-footer">
        <button type="button" className="cm-btn cm-btn-secondary" onClick={goBack}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
      </div>
    </div>
  );
};

export default AuthStep;
